import React, { useState, useEffect, useRef } from 'react';
import { getCssVar } from '../contexts/ThemeContext';
import { trackMiniAppEvent, trackMiniAppTimeSpent } from '../utils/analytics';

export default function MathQuizApp({ onAppFocus }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [gameMode, setGameMode] = useState('practice'); // 'practice', 'timed'
  const [timeLeft, setTimeLeft] = useState(60);
  const [isGameActive, setIsGameActive] = useState(false);
  const [difficulty, setDifficulty] = useState('easy'); // 'easy', 'medium', 'hard'
  const [operations, setOperations] = useState(['+', '-']); // '+', '-', '*', '/'
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [questionCount, setQuestionCount] = useState(10); // Default 10 questions
  
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const startTimeRef = useRef(null);

  // Difficulty configurations
  const difficultyConfig = {
    easy: {
      range: [1, 10],
      operations: ['+', '-'],
      timeLimit: 30
    },
    medium: {
      range: [1, 50],
      operations: ['+', '-', '*'],
      timeLimit: 20
    },
    hard: {
      range: [1, 100],
      operations: ['+', '-', '*', '/'],
      timeLimit: 15
    }
  };

  const generateQuestion = () => {
    const config = difficultyConfig[difficulty];
    const operation = config.operations[Math.floor(Math.random() * config.operations.length)];
    let num1, num2, answer;

    switch (operation) {
      case '+':
        num1 = Math.floor(Math.random() * config.range[1]) + config.range[0];
        num2 = Math.floor(Math.random() * config.range[1]) + config.range[0];
        answer = num1 + num2;
        break;
      case '-':
        num1 = Math.floor(Math.random() * config.range[1]) + config.range[0];
        num2 = Math.floor(Math.random() * config.range[1]) + config.range[0];
        // Ensure positive result for easy difficulty
        if (difficulty === 'easy' && num1 < num2) {
          [num1, num2] = [num2, num1];
        }
        answer = num1 - num2;
        break;
      case '*':
        num1 = Math.floor(Math.random() * Math.min(12, config.range[1])) + 1;
        num2 = Math.floor(Math.random() * Math.min(12, config.range[1])) + 1;
        answer = num1 * num2;
        break;
      case '/':
        // Generate division with whole number results
        num2 = Math.floor(Math.random() * 12) + 1;
        answer = Math.floor(Math.random() * 12) + 1;
        num1 = num2 * answer;
        break;
      default:
        num1 = 1;
        num2 = 1;
        answer = 2;
    }

    return {
      num1,
      num2,
      operation,
      answer,
      question: `${num1} ${operation} ${num2} = ?`
    };
  };

  const startGame = () => {
    // Track quiz start
    trackMiniAppEvent.mathquiz.quizStarted(gameMode, difficulty, questionCount);
    
    // Record start time for tracking
    startTimeRef.current = Date.now();
    
    setScore(0);
    setTotalQuestions(0);
    setCorrectAnswers(0);
    setStreak(0);
    setFeedback('');
    setIsCorrect(null);
    setIsGameActive(true);
    setShowStats(false);
    
    if (gameMode === 'timed') {
      setTimeLeft(difficultyConfig[difficulty].timeLimit);
    }
    
    setCurrentQuestion(generateQuestion());
    setUserAnswer('');
    
    if (onAppFocus) onAppFocus();
    
    // Focus on input
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
  };

  const checkAnswer = () => {
    if (!currentQuestion || userAnswer === '') return;

    const userNum = parseInt(userAnswer);
    const isAnswerCorrect = userNum === currentQuestion.answer;
    
    // Track question answered
    trackMiniAppEvent.mathquiz.questionAnswered(isAnswerCorrect, difficulty, currentQuestion.operation);
    
    setIsCorrect(isAnswerCorrect);
    const newTotalQuestions = totalQuestions + 1;
    setTotalQuestions(newTotalQuestions);
    
    if (isAnswerCorrect) {
      setScore(prev => prev + 10);
      setCorrectAnswers(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) {
          setBestStreak(newStreak);
        }
        // Track streak updates
        trackMiniAppEvent.mathquiz.streakUpdated(newStreak);
        return newStreak;
      });
      setFeedback('✅ Correct! Great job!');
    } else {
      setStreak(0);
      setFeedback(`❌ Wrong! The answer was ${currentQuestion.answer}`);
    }

    // Check if we've reached the question count limit
    if (newTotalQuestions >= questionCount) {
      setTimeout(() => {
        endGame();
      }, 1500);
    } else {
      // Generate next question after a short delay
      setTimeout(() => {
        if (isGameActive) {
          setCurrentQuestion(generateQuestion());
          setUserAnswer('');
          setFeedback('');
          setIsCorrect(null);
          if (inputRef.current) inputRef.current.focus();
        }
      }, 1500);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      checkAnswer();
    }
  };

  const endGame = () => {
    // Track quiz completion
    const accuracy = getAccuracy();
    trackMiniAppEvent.mathquiz.quizCompleted(score, accuracy, totalQuestions, difficulty);
    
    // Track time spent
    if (startTimeRef.current) {
      trackMiniAppTimeSpent('mathquiz', startTimeRef.current);
      startTimeRef.current = null;
    }
    
    setIsGameActive(false);
    setShowStats(true);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  // Timer effect for timed mode
  useEffect(() => {
    if (gameMode === 'timed' && isGameActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameMode, isGameActive, timeLeft]);

  const getAccuracy = () => {
    return totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  };

  const getGrade = () => {
    const accuracy = getAccuracy();
    if (accuracy >= 90) return 'A+';
    if (accuracy >= 80) return 'A';
    if (accuracy >= 70) return 'B';
    if (accuracy >= 60) return 'C';
    if (accuracy >= 50) return 'D';
    return 'F';
  };

  // Wrapper functions for settings changes with analytics
  const handleGameModeChange = (newMode) => {
    trackMiniAppEvent.mathquiz.modeChanged(newMode);
    setGameMode(newMode);
  };

  const handleDifficultyChange = (newDifficulty) => {
    trackMiniAppEvent.mathquiz.difficultyChanged(newDifficulty);
    setDifficulty(newDifficulty);
  };

  const handleQuestionCountChange = (newCount) => {
    trackMiniAppEvent.mathquiz.questionCountChanged(newCount);
    setQuestionCount(newCount);
  };

  if (showStats) {
    return (
      <div style={{
        padding: '20px',
        background: 'var(--color-bg-card)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px var(--color-shadow)',
        margin: '10px 0'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: 'var(--color-text)' }}>📊 Quiz Results</h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '12px',
            background: 'var(--color-bg)',
            borderRadius: '8px',
            border: '2px solid var(--color-border)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-primary)' }}>
              {score}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Score</div>
          </div>
          
          <div style={{
            textAlign: 'center',
            padding: '12px',
            background: 'var(--color-bg)',
            borderRadius: '8px',
            border: '2px solid var(--color-border)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-success)' }}>
              {getAccuracy()}%
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Accuracy</div>
          </div>
          
          <div style={{
            textAlign: 'center',
            padding: '12px',
            background: 'var(--color-bg)',
            borderRadius: '8px',
            border: '2px solid var(--color-border)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-warning)' }}>
              {bestStreak}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Best Streak</div>
          </div>
          
          <div style={{
            textAlign: 'center',
            padding: '12px',
            background: 'var(--color-bg)',
            borderRadius: '8px',
            border: '2px solid var(--color-border)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-info)' }}>
              {getGrade()}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Grade</div>
          </div>
          
          <div style={{
            textAlign: 'center',
            padding: '12px',
            background: 'var(--color-bg)',
            borderRadius: '8px',
            border: '2px solid var(--color-border)'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-secondary)' }}>
              {totalQuestions}/{questionCount}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>Questions</div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={startGame}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-bg-card)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            🎯 Play Again
          </button>
          
          <button
            onClick={() => setShowStats(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--color-bg)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ⚙️ Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px',
      background: 'var(--color-bg-card)',
      borderRadius: '12px',
      boxShadow: '0 4px 12px var(--color-shadow)',
      margin: '10px 0'
    }}>
      <h3 style={{ margin: '0 0 16px 0', color: 'var(--color-text)' }}>🧮 Math Quiz</h3>
      
      {!isGameActive ? (
        // Settings/Start Screen
        <div>
          {/* Game Mode Selection */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'var(--color-text)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Game Mode:
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { value: 'practice', label: '🏃 Practice Mode', desc: 'No time limit' },
                { value: 'timed', label: '⏱️ Timed Mode', desc: 'Beat the clock!' }
              ].map(mode => (
                <button
                  key={mode.value}
                  onClick={() => handleGameModeChange(mode.value)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: gameMode === mode.value ? 'var(--color-primary)' : 'var(--color-bg)',
                    color: gameMode === mode.value ? 'var(--color-bg-card)' : 'var(--color-text)',
                    border: `2px solid var(--color-border)`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    minWidth: '120px'
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{mode.label}</div>
                  <div style={{ fontSize: '10px', opacity: 0.8 }}>{mode.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty Selection */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'var(--color-text)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Difficulty:
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { value: 'easy', label: '😊 Easy', desc: 'Numbers 1-10, + and -' },
                { value: 'medium', label: '😐 Medium', desc: 'Numbers 1-50, +, -, ×' },
                { value: 'hard', label: '😰 Hard', desc: 'Numbers 1-100, +, -, ×, ÷' }
              ].map(level => (
                <button
                  key={level.value}
                  onClick={() => handleDifficultyChange(level.value)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: difficulty === level.value ? 'var(--color-primary)' : 'var(--color-bg)',
                    color: difficulty === level.value ? 'var(--color-bg-card)' : 'var(--color-text)',
                    border: `2px solid var(--color-border)`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    textAlign: 'left',
                    minWidth: '120px'
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{level.label}</div>
                  <div style={{ fontSize: '10px', opacity: 0.8 }}>{level.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Question Count Selection */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: 'var(--color-text)', fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Number of Questions:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="range"
                min="5"
                max="50"
                value={questionCount}
                onChange={(e) => handleQuestionCountChange(parseInt(e.target.value))}
                style={{
                  flex: 1,
                  minWidth: '150px',
                  accentColor: 'var(--color-primary)'
                }}
              />
              <span style={{ 
                color: 'var(--color-text)', 
                fontSize: '16px', 
                fontWeight: 'bold',
                minWidth: '40px',
                textAlign: 'center'
              }}>
                {questionCount}
              </span>
              <div style={{ 
                display: 'flex', 
                gap: '4px',
                flexWrap: 'wrap'
              }}>
                {[5, 10, 15, 20, 25, 30].map(count => (
                  <button
                    key={count}
                    onClick={() => handleQuestionCountChange(count)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: questionCount === count ? 'var(--color-primary)' : 'var(--color-bg)',
                      color: questionCount === count ? 'var(--color-bg-card)' : 'var(--color-text)',
                      border: `1px solid var(--color-border)`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: questionCount === count ? 'bold' : 'normal'
                    }}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={startGame}
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--color-success)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              width: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            🚀 Start Quiz
          </button>
        </div>
      ) : (
        // Game Screen
        <div>
          {/* Game Stats */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '12px',
            background: 'var(--color-bg)',
            borderRadius: '8px',
            border: '2px solid var(--color-border)'
          }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-text)', fontSize: '14px' }}>
                Score: <strong style={{ color: 'var(--color-primary)' }}>{score}</strong>
              </span>
              <span style={{ color: 'var(--color-text)', fontSize: '14px' }}>
                Streak: <strong style={{ color: 'var(--color-warning)' }}>{streak}</strong>
              </span>
              <span style={{ color: 'var(--color-text)', fontSize: '14px' }}>
                Questions: <strong>{totalQuestions}</strong>/<strong>{questionCount}</strong>
              </span>
            </div>
            
            {gameMode === 'timed' && (
              <div style={{
                padding: '4px 8px',
                backgroundColor: timeLeft <= 10 ? 'var(--color-danger)' : 'var(--color-primary)',
                color: 'white',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                minWidth: '40px',
                textAlign: 'center'
              }}>
                {timeLeft}s
              </div>
            )}
          </div>

          {/* Question */}
          {currentQuestion && (
            <div style={{
              textAlign: 'center',
              marginBottom: '20px',
              padding: '20px',
              background: 'var(--color-bg)',
              borderRadius: '8px',
              border: '2px solid var(--color-border)'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: 'var(--color-text)',
                marginBottom: '16px'
              }}>
                {currentQuestion.question}
              </div>
              
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                <input
                  ref={inputRef}
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={{
                    fontSize: '24px',
                    padding: '8px 12px',
                    border: `3px solid ${isCorrect === true ? 'var(--color-success)' : isCorrect === false ? 'var(--color-danger)' : 'var(--color-border)'}`,
                    borderRadius: '6px',
                    width: '120px',
                    textAlign: 'center',
                    backgroundColor: 'var(--color-bg-card)',
                    color: 'var(--color-text)'
                  }}
                  placeholder="?"
                  disabled={feedback !== ''}
                />
                <button
                  onClick={checkAnswer}
                  disabled={userAnswer === '' || feedback !== ''}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: userAnswer === '' || feedback !== '' ? 'var(--color-bg)' : 'var(--color-primary)',
                    color: userAnswer === '' || feedback !== '' ? 'var(--color-text-secondary)' : 'var(--color-bg-card)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: userAnswer === '' || feedback !== '' ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Check
                </button>
              </div>
            </div>
          )}

          {/* Feedback */}
          {feedback && (
            <div style={{
              textAlign: 'center',
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: isCorrect ? 'var(--color-success)' : 'var(--color-danger)',
              color: 'white',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              {feedback}
            </div>
          )}

          {/* Controls */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center'
          }}>
            <button
              onClick={endGame}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-danger)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              🏁 End Quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 